import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { observer } from "mobx-react-lite";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  HStack,
  useToast,
  Spinner,
  Center,
  Text,
} from "@chakra-ui/react";
import { useStores } from "../../store/store_context";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek);

const COLUMN_CONFIG = [
  { key: "dateCreated", title: "Время" },
  { key: "report_name", title: "Отчёт" },
  { key: "status", title: "Статус" },
  { key: "data", title: "Сообщение" },
  { key: "reference", title: "источник" },
];

const formatDateTime = (dateString) => {
  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return dateString;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return dateString;
  }
};

const translateStatus = (status) => {
  if (!status) return status;

  const statusMap = {
    'success': 'успех',
    'error': 'ошибка',
  };

  const lowerStatus = status.toLowerCase();
  return statusMap[lowerStatus] || status;
};

const formatTextWithLineBreaks = (text) => {
  if (!text) return text;

  const lines = text.split('\n');

  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

const LogTable = observer(() => {
  const { pageStore } = useStores();
  const [isLoading, setIsLoading] = useState(true);
  const loaderRef = useRef(null);
  const toast = useToast();

  const [page, setPage] = useState(0); // номер страницы (0 — текущая неделя)
  const [logs, setLogs] = useState([]);

  const loadLogs = async (pageIndex) => {
    setIsLoading(true);

    const endOfWeek = dayjs().endOf("isoWeek").subtract(7 * pageIndex, "day");
    const startOfWeek = endOfWeek.subtract(6, "day"); // 6, чтобы охватить 7 дней

    const date_from = startOfWeek.format("YYYY-MM-DD");
    const date_to = endOfWeek.format("YYYY-MM-DD");
    try {
      const newLogs = await pageStore.getLogs(date_from, date_to);
      setLogs((prev) => {
        const safeNew = Array.isArray(pageStore.data_logs) ? pageStore.data_logs : [];

        const merged = [
          ...safeNew,
        ];
        return merged;
      });

    } catch (error) {
      toast({
        title: "Ошибка загрузки",
        description: "Не удалось загрузить логи",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(0);
  }, []);

  useEffect(() => {
    if (page >= 8) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadLogs(nextPage);
        }
      },
      { threshold: 1 }
    );

    const current = loaderRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [page, isLoading]);


  const availableColumns = useMemo(() => {
    if (!Array.isArray(pageStore.data_logs) || pageStore.data_logs.length === 0) {
      return COLUMN_CONFIG;
    }

    const firstItem = pageStore.data_logs[0];
    if (!firstItem || typeof firstItem !== 'object') {
      return COLUMN_CONFIG;
    }

    const filtered = COLUMN_CONFIG.filter(
      col => firstItem.hasOwnProperty(col.key)
    );

    return filtered.map(col => ({
      ...col,
    }));
  }, [pageStore.data_logs]);

  const renderCellValue = useCallback((columnKey, value) => {
    if (columnKey === "dateCreated" && value) {
      return formatDateTime(value);
    }

    if (columnKey === "status" && value) {
      return translateStatus(value);
    }

    if (columnKey === "data" && value) {
      return formatTextWithLineBreaks(value);
    }

    return value;
  }, []);

  if (isLoading) {
    return (
      <Center height="200px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  if (!pageStore.data_logs || !Array.isArray(pageStore.data_logs) || pageStore.data_logs.length === 0) {
    if (pageStore.data_logs.result == "has no permission") {
      return (
        <Center height="200px">
          <Text textColor="white" fontSize="lg">у вас нет прав для просмотра данного раздела</Text>
        </Center>
      );
    }
    return (
      <Center height="200px">
        <Text textColor="white" fontSize="lg">Нет данных для отображения</Text>
      </Center>
    );
  }

  return (
    <Box
      maxHeight="95vh"
      overflowY="auto"
      borderRadius="md"
      borderWidth={1}
      borderColor="gray.300"
      position="relative"
      bg="#222"
    >
      <Table tableLayout="fixed" width="100%" minWidth="1500px" variant="simple" color="white">
        <Thead
          position="sticky"
          top="0"
          zIndex="2"
          bg="#111" // фиксированный фон, чтобы не просвечивалось при скролле
        >
          <Tr>
            {availableColumns.map((column) => (
              <Th
                key={column.key}
                color="white"
                cursor="pointer"
                px={4}
                py={2}
                fontWeight={700}
                borderBottom="2px solid #555"
              >
                {column.title}
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody>
          <Tr key={0} bg={"#444"}>
            <Td
            key={"dateCreated"}
            px={4}
            py={2}
            whiteSpace="normal"
            wordBreak="break-word"
            >
              Всего строк
            </Td>
            <Td
            key={"report_name"}
            px={4}
            py={2}
            whiteSpace="normal"
            wordBreak="break-word"
            >
              {logs.length}
            </Td>
            <Td
              key={"status"}
              px={4}
              py={2}
              whiteSpace="normal"
              wordBreak="break-word">
            </Td>
            <Td
            key={"data"}
            px={4}
            py={2}
            whiteSpace="normal"
            wordBreak="break-word">
            </Td>
            <Td
            key={"reference"}
            px={4}
            py={2}
            whiteSpace="normal"
            wordBreak="break-word"></Td>
          </Tr>
          {logs.map((row, index) => (
            <Tr key={index + 1} bg={index + 1 % 2 === 0 ? "#444" : "#333"}>
              {availableColumns.map((column) => (
                <Td
                  key={column.key}
                  px={4}
                  py={2}
                  whiteSpace="normal"
                  wordBreak="break-word"
                >
                  {renderCellValue(column.key, row[column.key])}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Футер с кнопками */}
      <Box
        position="fixed"
        bottom="0"
        left="0"
        width="100%"
        zIndex="10"
        bg="#111"
        borderTop="1px solid #555"
        textAlign="center"
        py={3}
        color="#999"
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap="12px"
      >
        <Button
          size="sm"
          bg="#222"
          color="#eee"
          _hover={{ bg: "#333" }}
          isDisabled={page === 0}
          onClick={() => {
            const newPage = Math.max(0, page - 1);
            setPage(newPage);
            loadLogs(newPage);
          }}
        >
          ← Назад
        </Button>

        <Text fontWeight={600} color="#ccc">
          Неделя {page + 1} из 8
        </Text>

        <Button
          size="sm"
          bg="#222"
          color="#eee"
          _hover={{ bg: "#333" }}
          isDisabled={page >= 7}
          onClick={() => {
            const newPage = Math.min(7, page + 1);
            setPage(newPage);
            loadLogs(newPage);
          }}
        >
          Вперёд →
        </Button>
      </Box>
    </Box>
  );
});

export default LogTable;